import { CronSelections, DaysBySelections } from '../../enums/schedules/cron-prop-selections';

/**
 * Fine-grained schedule definition for a ScheduleTask. Converted to a standard
 * 5-field cron string by `BuildCronString`: `"minutes hours dayOfMonth month dayOfWeek[L]"`.
 */
export class CronScheduleProps {
	/**
	 * How the minutes field is interpreted.
	 * 'Every' = run every N minutes where N = minutes[0].
	 * 'At' = run at the specific minute(s) listed in minutes[].
	 * @default CronSelections.Every
	 */
	minuteSelection: CronSelections = CronSelections.Every;

	/**
	 * Minute values (0–59).
	 * 'Every' mode: [N] where N is the interval (e.g. [15] = every 15 min).
	 * 'At' mode: exact minutes to run at.
	 * @default [1]
	 */
	minutes: number[] = [1];

	/**
	 * How the hours field is interpreted.
	 * 'Every' = run every N hours where N = hours[0].
	 * 'At' = run at the specific hour(s) listed in hours[].
	 * @default CronSelections.Every
	 */
	hourSelection: CronSelections = CronSelections.Every;

	/**
	 * Hour values (0–23).
	 * 'Every' mode: [N] where N is the interval.
	 * 'At' mode: exact hours to run at.
	 * Empty = every hour (*).
	 * @default []
	 */
	hours: number[] = [];

	/**
	 * How the day field is interpreted (only used when daysBy = 'Month').
	 * 'Every' = run every N days.
	 * 'At' = run on the specific day(s) of the month listed in days[].
	 * @default CronSelections.Every
	 */
	daySelection: CronSelections = CronSelections.Every;

	/**
	 * Day values.
	 * When daysBy='Week': day-of-week (0=Sun, 1=Mon, …, 6=Sat, 7=Sun).
	 * When daysBy='Month': day-of-month (1–31); with daySelection='At' maps to exact days.
	 * Empty = every day (*).
	 * @default []
	 */
	days: number[] = [];

	/**
	 * Controls whether days[] refers to day-of-week or day-of-month.
	 * 'Week' = populates the dayOfWeek cron field.
	 * 'Month' = populates the dayOfMonth cron field.
	 * @default DaysBySelections.Week
	 */
	daysBy: DaysBySelections = DaysBySelections.Week;

	/**
	 * Specific months to run (1=Jan … 12=Dec).
	 * Empty = every month (*).
	 * @default []
	 */
	month: number[] = [];

	/**
	 * When true, appends 'L' to the day-of-month cron field, meaning the last day of the month.
	 * @default false
	 */
	isLastDayOfTheMonth = false;

	constructor(scheduleCalendarProps?: Partial<CronScheduleProps>) {
		if (scheduleCalendarProps) {
			Object.assign(this, scheduleCalendarProps);
		}
	}
}
